const REMEMBER_ME_KEY = 'ggb.auth.remember-me';

type AuthStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> & {
  key?: (index: number) => string | null;
  length?: number;
};

const unavailableStore: AuthStore = {
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
};

function browserStores() {
  if (typeof window === 'undefined') return { local: unavailableStore, session: unavailableStore };
  return { local: window.localStorage, session: window.sessionStorage };
}

function isAuthStorageKey(key: string) {
  return key === REMEMBER_ME_KEY || key.startsWith('sb-') || key.includes('auth-token');
}

export function readRememberMe(local: AuthStore = browserStores().local) {
  return local.getItem(REMEMBER_ME_KEY) !== 'session';
}

export function setRememberMe(remember: boolean, local: AuthStore = browserStores().local) {
  local.setItem(REMEMBER_ME_KEY, remember ? 'local' : 'session');
}

/** Keep signed-in users across visits unless they explicitly opt out on the next sign-in. */
export function ensureRememberedAuthPreference(local: AuthStore = browserStores().local) {
  if (local.getItem(REMEMBER_ME_KEY) == null) local.setItem(REMEMBER_ME_KEY, 'local');
}

/** Move Supabase auth keys into the preferred store so a preference flip cannot strand a session. */
export function reconcileAuthStorage(
  local: AuthStore = browserStores().local,
  session: AuthStore = browserStores().session,
) {
  ensureRememberedAuthPreference(local);
  const preferred = readRememberMe(local) ? local : session;
  const fallback = readRememberMe(local) ? session : local;
  const length = fallback.length ?? 0;
  for (let index = 0; index < length; index += 1) {
    const key = fallback.key?.(index);
    if (!key || !isAuthStorageKey(key) || key === REMEMBER_ME_KEY) continue;
    const value = fallback.getItem(key);
    if (value == null) continue;
    if (preferred.getItem(key) == null) preferred.setItem(key, value);
    fallback.removeItem(key);
    // Removing shifts indexes; restart so we do not skip keys.
    index = -1;
  }
}

export function createAuthStorage(
  local: AuthStore = browserStores().local,
  session: AuthStore = browserStores().session,
): AuthStore {
  reconcileAuthStorage(local, session);
  return {
    getItem(key) {
      const preferred = readRememberMe(local) ? local : session;
      const fallback = readRememberMe(local) ? session : local;
      const primary = preferred.getItem(key);
      if (primary != null) return primary;
      const secondary = fallback.getItem(key);
      if (secondary == null) return null;
      preferred.setItem(key, secondary);
      fallback.removeItem(key);
      return secondary;
    },
    setItem(key, value) {
      const persistent = readRememberMe(local);
      const target = persistent ? local : session;
      const other = persistent ? session : local;
      target.setItem(key, value);
      other.removeItem(key);
    },
    removeItem(key) {
      local.removeItem(key);
      session.removeItem(key);
    },
  };
}
